<template>
  <v-dialog v-model="isActive" fullscreen hide-overlay transition="dialog-bottom-transition" class="container" scrollable>
    <v-card class="content">
      <div class="header">
        <div>
          <h2>Pesquisar serviço</h2>
          <div>Atrelar serviço ao orçamento selecionado</div>
        </div>
        <div class="actions">
          <div @click="handleAddService()" class="add">
            <v-icon color="primary">mdi-plus</v-icon>
          </div>
          <div @click="close()" class="return">
            <v-icon color="white">mdi-close</v-icon>
          </div>
        </div>
      </div>
      <div class="search">
        <v-text-field v-model="term" v-debounce:1s="search" label="Pesquisar serviço" outlined hide-details clearable></v-text-field>
      </div>
      <div class="list-container">
        <v-list-item-group class="service-list">
          <LazyList :items="filteredServices" @load-more="loadMoreData" :has-more-data="hasMoreData">
            <template v-slot:default="{ items }">
              <div v-for="service in items" :key="service.id" style="height: 86px; display: flex">
                <v-list-item @click="updateSelectedServices(service.id, !selectedServiceIds.includes(service.id))">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>{{ service.description }}</v-list-item-title>
                      <v-list-item-subtitle>{{ formatCurrency(service.value) }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn class="add-btn" icon>
                        <v-icon color="green" small>mdi-plus</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </div>
            </template>
          </LazyList>

          <div class="loader" v-if="isLoading">
            <v-row v-for="index in 15" :key="index">
              <v-col cols="12">
                <v-skeleton-loader type="list-item-two-line"></v-skeleton-loader>
              </v-col>
            </v-row>
          </div>

          <div v-if="filteredServices.length === 0" class="no-results">Nenhum serviço encontrado</div>
        </v-list-item-group>

        <div v-if="selectedServices.length !== 0" class="selected-services">
          <span class="label">Serviços selecionados:</span>
          <v-simple-table
            fixed-header
            height="300px"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Obrigatório
                  </th>
                  <th class="text-left">
                    Descrição/Valor
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="service in selectedServices"
                  :key="service.id"
                >
                  <td>
                    <v-switch
                      v-model="service.required"
                      color="primary"
                    ></v-switch>
                  </td>
                  <td>
                    <div>
                      <span>{{ service.description }}</span>
                    </div>
                    <div>
                      <span>{{ formatCurrency(service.value) }}</span>
                    </div>
                  </td>
                  <td>
                    <v-btn icon @click="updateSelectedServices(service.id, false)">
                      <v-icon color="red" small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
        <div class="set-filters-action">
          <v-btn block color="primary" bottom :disabled="selectedServiceCount === 0" @click="addSelectedServices">
            Adicionar {{ selectedServiceCount }} serviço(s)
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-bottom-sheet v-model="isAddService" persistent>
      <div class="bottom-sheet-content">
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Novo serviço</span>
          <v-btn icon @click="closeManageModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-text-field v-model="service.code" label="Código" outlined></v-text-field>
        <v-text-field v-model="service.description" label="Nome" outlined></v-text-field>
        <v-text-field v-model="service.value" ref="value" label="Valor" outlined v-money="valor"></v-text-field>
        <v-text-field v-model="service.commission" ref="commission" label="Comissão" outlined v-money="percentual"></v-text-field>
        <v-text-field v-model="service.estimated_minutes" label="Minutos estimados" outlined type="number"></v-text-field>
        <v-btn color="primary" block @click="addService" :disabled="!service.description || !service.value">Salvar</v-btn>
      </div>
    </v-bottom-sheet>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';

import ServiceService from '@/services/api/service';
import LazyList from '@/components/Common/LazyList';

export default {
  name: 'ServiceSelector',
  emits: ['close', 'select'],
  props: {
    isActive: {
      type: Boolean,
      default: false
    }
  },
  components: {
    LazyList
  },
  data() {
    return {
      term: '',
      services: [],
      selectedServiceIds: [],
      selectedServices: [],
      currentPage: 1,
      itemsPerPage: 15,
      isLoading: false,
      hasMoreData: false,
      isAddService: false,
      service: {
        code: '',
        description: null,
        value: '0',
        commission: null,
        estimated_minutes: null
      },
      valor: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: true
      },
      percentual: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: ' %',
        precision: 2,
        masked: true
      }
    };
  },
  watch: {
    term() {
      this.currentPage = 1;
      this.services = [];
    },
    service: {
      handler(newValue) {
        if (newValue.commission === null) {
          return;
        }

        const commission = parseFloat(newValue.commission.replace('%', '').replace('.', '').replace(',', '.').trim());

        if (commission > 100) {
          this.commission = '100.00';
          this.$refs.commission.$el.getElementsByTagName('input')[0].value = this.commission;
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState('Budget', ['selectedBudget']),
    filteredServices() {
      if (!this.services) {
        return [];
      }

      return this.services.filter((service) => !this.selectedServiceIds.includes(service.id));
    },
    selectedServiceCount() {
      return this.selectedServiceIds.length;
    }
  },
  methods: {
    close() {
      this.term = '';
      this.selectedServiceIds = [];
      this.selectedServices = [];
      this.services = [];

      this.$emit('close');
    },
    addSelectedServices() {
      this.$emit('select', this.selectedServices);
      this.close();
    },
    formatCurrency(value) {
      return value ? parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    },
    async search() {
      if (this.term.trim() === '' || this.term.length < 3) {
        this.services = [];
        return;
      }

      this.isLoading = true;

      const queryParams = {
        description: this.term,
        size: this.itemsPerPage,
        page: this.currentPage,
        budget_id: this.selectedBudget.id
      };

      try {
        const response = await ServiceService.search(queryParams);
        const responseData = response.data;

        if (responseData && responseData.rows) {
          const newServices = responseData.rows.filter(service => !this.services.find(s => s.id === service.id));
          this.services = [...this.services, ...newServices];
        }

        this.currentPage = responseData.currentPage || 1;
        const totalPages = responseData.totalPages || 1;
        this.hasMoreData = this.currentPage < totalPages;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    updateSelectedServices(serviceId, value) {
      if (value) {
        if (!this.selectedServiceIds.includes(serviceId)) {
          const selectedService = this.services.find(service => service.id === serviceId);
          if (selectedService) {
            selectedService.required = true;
            this.selectedServiceIds.push(serviceId);
            this.selectedServices.push(selectedService);
          }
        }
      } else {
        this.selectedServiceIds = this.selectedServiceIds.filter(id => id !== serviceId);
        this.selectedServices = this.selectedServices.filter(service => service.id !== serviceId);
      }
    },
    loadMoreData() {
      if (this.hasMoreData && !this.isLoading) {
        this.currentPage += 1;
        this.search();
      }
    },
    handleAddService() {
      this.isAddService = true;
    },
    addService() {
      this.isAddService = false;
      this.isLoading = true;

      ServiceService.create({
        code: this.service.code,
        description: this.service.description,
        value: parseFloat(this.service.value.replace('R$ ', '').replace('.', '').replace(',', '.').trim()),
        commission: parseFloat(this.service.commission.replace('%', '').replace('.', '').replace(',', '.').trim()),
        estimated_minutes: this.service.estimated_minutes
      })
        .then((response) => {
          this.services = [response.data, ...this.services];
          this.cleanService();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    closeManageModal() {
      this.isAddService = false;
    },
    cleanService() {
      this.service = {
        code: '',
        description: null,
        value: '0',
        commission: null,
        estimated_minutes: null
      };

      this.$refs.value.$el.getElementsByTagName('input')[0].value = '0.00';
      this.$refs.commission.$el.getElementsByTagName('input')[0].value = '0.00';
    }
  }
};
</script>

<style lang="scss" scoped>
.content {
  flex: 1;
  display: flex;
  flex-direction: column;

  .header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 16px;
    background-color: #3e515f;
    padding: 20px 20px;
    color: #fff;
    z-index: 1;
    border-radius: 0px !important;

    .actions {
      display: flex;
      gap: 16px;
      justify-content: flex-end;
      align-items: center;

      .add,
      .return {
        border-radius: 50%;
        padding: 10px;
      }

      .add {
        background-color: white;
      }

      .return {
        background-color: #2d3c47;
      }
    }
  }

  .search {
    background-color: #f5f5f5;
    padding: 20px;
  }

  .list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .service-list {
      flex: 1;
      max-height: 250px;
      overflow-y: auto;
      overflow-x: hidden;

      .add-btn {
        min-width: 0;
        padding: 5px;
        border-radius: 50%;
        border: 1px solid #ccc;
        color: #48b38b !important;
      }

      .no-results {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        text-align: center;
        margin-top: 50px;
      }
    }

    .selected-services {
      padding: 16px;

      .label {
        font-weight: bold;
        margin-right: 8px;
      }
    }

    .set-filters-action {
      padding: 0 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #fff;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      margin-bottom: 12px;
      margin-top: 12px;
      padding-top: 12px;
    }
  }
}

.v-btn--icon {
  margin: 0 !important;
}

.v-btn--icon.v-size--default {
  height: 24px !important;
  width: 24px !important;
}

.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none !important;
}

.bottom-sheet-content {
  padding: 16px;

  .bottom-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .bottom-sheet-title {
      font-size: 18px;
      font-weight: 500;
    }
  }
}
</style>
